import React, { useEffect, useState } from "react";
import { api, formatCLP, BACKEND_URL } from "../lib/api";
import { Truck, ExternalLink, Package, Calendar } from "lucide-react";

const MisPedidos = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => setOrders([])).finally(() => setLoading(false));
  }, []);

  const proofSrc = (o) => {
    if (!o.shipping_proof_url) return null;
    if (o.shipping_proof_url.startsWith("http")) return o.shipping_proof_url;
    return `${BACKEND_URL}${o.shipping_proof_url}`;
  };

  return (
    <div data-testid="mis-pedidos-page" className="min-h-screen bg-cream pt-12 pb-24">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-brand-ink mb-2">
          Mis <span className="font-accent italic text-mint-deep">pedidos</span>
        </h1>
        <p className="text-gray-600 mb-8">Aquí puedes revisar el estado de tus pedidos, datos de envío y seguimiento.</p>

        {loading ? <p className="text-gray-400">Cargando...</p> :
          orders.length === 0 ? (
            <div className="bg-white border-2 border-black p-12 text-center">
              <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aún no tienes pedidos.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {orders.map((o) => (
                <div key={o.id} data-testid={`mi-pedido-${o.id}`} className="bg-white border-2 border-black p-5">
                  <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Pedido #{o.id.slice(0, 8).toUpperCase()}</p>
                      <p className="text-xs text-gray-500 inline-flex items-center gap-1 mt-1">
                        <Calendar className="h-3 w-3" /> {new Date(o.created_at).toLocaleString("es-CL")}
                      </p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-3 py-1 border-2 border-black font-bold ${statusColor(o.status)}`}>
                      {o.status}
                    </span>
                  </div>

                  {/* Items */}
                  <div className="text-sm space-y-1 mb-3">
                    {o.items.map((it, i) => (
                      <p key={i}>{it.quantity}× {it.name}</p>
                    ))}
                  </div>

                  {/* Totals breakdown */}
                  {(o.subtotal !== null && o.subtotal !== undefined && (o.discount_global_pct > 0 || o.discount_code)) ? (
                    <div className="bg-mint-light/50 border border-mint-deep/40 p-3 mb-3 text-xs space-y-1">
                      <div className="flex justify-between"><span>Subtotal</span><span>{formatCLP(o.subtotal)}</span></div>
                      {o.discount_global_pct > 0 && (
                        <div className="flex justify-between text-mint-deep">
                          <span>Descuento global (−{o.discount_global_pct}%)</span>
                          <span>−{formatCLP(o.subtotal * o.discount_global_pct / 100)}</span>
                        </div>
                      )}
                      {o.discount_code && (
                        <div className="flex justify-between text-mint-deep">
                          <span>Cupón {o.discount_code} (−{o.discount_code_pct}%)</span>
                          <span>−{formatCLP(o.subtotal * o.discount_code_pct / 100)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold pt-1 border-t border-black/20">
                        <span>Total</span>
                        <span>{o.total !== null ? formatCLP(o.total) : "—"}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="font-heading text-lg font-bold mb-2">{o.total !== null && o.total !== undefined ? formatCLP(o.total) : "A cotizar"}</p>
                  )}

                  {/* Shipping info */}
                  {(o.shipping_company || o.tracking_code || proofSrc(o)) && (
                    <div className="mt-4 pt-4 border-t-2 border-black">
                      <div className="flex items-center gap-2 mb-3">
                        <Truck className="h-4 w-4 text-mint-deep" />
                        <p className="text-xs uppercase tracking-[0.18em] font-bold text-brand-ink">Información de envío</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {o.shipping_company && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Empresa</p>
                            <p className="text-brand-ink font-medium">{o.shipping_company}</p>
                          </div>
                        )}
                        {o.tracking_code && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Código de seguimiento</p>
                            <p className="text-brand-ink font-mono font-bold">
                              {o.tracking_url ? (
                                <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-mint-deep hover:underline inline-flex items-center gap-1" data-testid={`track-link-${o.id}`}>
                                  {o.tracking_code} <ExternalLink className="h-3 w-3" />
                                </a>
                              ) : o.tracking_code}
                            </p>
                          </div>
                        )}
                      </div>
                      {proofSrc(o) && (
                        <div className="mt-3">
                          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2">Foto del comprobante de envío</p>
                          <a href={proofSrc(o)} target="_blank" rel="noreferrer" data-testid={`ship-proof-${o.id}`}>
                            <img src={proofSrc(o)} alt="Comprobante envío" className="h-40 w-auto border-2 border-black hover:opacity-90 transition" />
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
};

const statusColor = (s) => {
  switch (s) {
    case "pendiente": return "bg-yolk-light text-yolk-deep";
    case "confirmado":
    case "en_proceso": return "bg-mint-light text-mint-deep";
    case "enviado": return "bg-cyan-100 text-cyan-700";
    case "entregado": return "bg-mint text-white";
    case "cancelado": return "bg-red-100 text-red-700";
    default: return "bg-gray-100 text-gray-700";
  }
};

export default MisPedidos;
